'use client';

import { c as _c } from "react/compiler-runtime";
import { useFeatureFlag } from '@posthog/next';
import { useEffect, useState } from 'react';
import AdSlot from '@/app/[locale]/(worlds)/[roomId]/components/AdSlot';
import OptimizedImage from '@/components/OptimizedImage';
import { FULL3D_MOBILE_BANNER_AD_SIZE } from '@/constants/ad';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useUAContext } from '@/context/UAContext';
import { usePlatformAdFree } from '@/hooks/usePlatformAdFree';
import { env } from '@/lib/runtime-env';
import { cn } from '@/lib/utils';
import { parseAdRefreshIntervalMs } from '@/schemas/ad-refresh';
import { parseAdTelemetryConfig } from '@/schemas/ad-telemetry';
const FULL3D_MOBILE_BANNER_FLAG = 'full3d-mobile-sticky-bottom-ad';
const AD_REQUEST_DELAY_MS = 5000;
function Full3dMobileStickyBannerAd() {
  const $ = _c(32);
  const {
    roomData
  } = useCheckEnterRoomContext();
  const {
    isMobileApp
  } = useUAContext();
  const {
    isAdFree
  } = usePlatformAdFree();
  const bannerFlag = useFeatureFlag(FULL3D_MOBILE_BANNER_FLAG);
  const [isAdRequestReady, setIsAdRequestReady] = useState(false);
  const [hasLoadedAd, setHasLoadedAd] = useState(false);
  const [isAdVisible, setIsAdVisible] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);
  const isBannerTreatment = bannerFlag?.variant === "treatment";
  const shouldShowBanner = isBannerTreatment && !isMobileApp && !isAdFree;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = hasRenderedAd => {
      setHasLoadedAd(hasRenderedAd);
      if (!hasRenderedAd) {
        setIsAdVisible(false);
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const handleSlotRenderEnded = t0;
  let t1;
  let t2;
  if ($[1] !== shouldShowBanner) {
    t1 = () => {
      if (!shouldShowBanner) {
        return;
      }
      const timeoutId = window.setTimeout(() => {
        setIsAdRequestReady(true);
      }, AD_REQUEST_DELAY_MS);
      return () => {
        window.clearTimeout(timeoutId);
      };
    };
    t2 = [shouldShowBanner];
    $[1] = shouldShowBanner;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== hasLoadedAd) {
    t3 = () => {
      if (!hasLoadedAd) {
        return;
      }
      let revealAdFrame;
      const prepareAdFrame = window.requestAnimationFrame(() => {
        revealAdFrame = window.requestAnimationFrame(() => {
          setIsAdVisible(true);
        });
      });
      return () => {
        window.cancelAnimationFrame(prepareAdFrame);
        if (revealAdFrame) {
          window.cancelAnimationFrame(revealAdFrame);
        }
      };
    };
    t4 = [hasLoadedAd];
    $[4] = hasLoadedAd;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  if (!shouldShowBanner) {
    return null;
  }
  const shouldRenderImage = !!roomData.image && !hasImageError;
  let t5;
  if ($[7] !== bannerFlag?.payload || $[8] !== isAdRequestReady || $[9] !== isAdVisible) {
    t5 = isAdRequestReady && <AdSlot adSurface="full3d-mobile-sticky-banner" telemetry={parseAdTelemetryConfig(bannerFlag?.payload)} gamAdUnitPath={env("NEXT_PUBLIC_GAM_FULL3D_MOBILE_BANNER_AD_UNIT_PATH")} sizes={FULL3D_MOBILE_BANNER_AD_SIZE} refreshIntervalMs={parseAdRefreshIntervalMs(bannerFlag?.payload)} className={cn("z-base absolute inset-0 flex items-center justify-center transition-opacity duration-300", isAdVisible ? "opacity-100" : "opacity-0")} slotClassName="leading-0" onSlotRenderEnded={handleSlotRenderEnded} showAdLabel={false} />;
    $[7] = bannerFlag?.payload;
    $[8] = isAdRequestReady;
    $[9] = isAdVisible;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = isAdVisible ? "pointer-events-none opacity-0" : "opacity-100";
  let t7;
  if ($[11] !== t6) {
    t7 = cn("bg-vive-neutral-0 z-raised text-vive-neutral-80 absolute inset-0 flex items-center gap-2 px-2 transition-opacity duration-300", t6);
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== roomData.image || $[14] !== shouldRenderImage) {
    t8 = shouldRenderImage && <OptimizedImage src={roomData.image} alt="" fill={true} sizes="36px" className="object-cover" onError={() => setHasImageError(true)} />;
    $[13] = roomData.image;
    $[14] = shouldRenderImage;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <div className="bg-vive-primary-40 relative size-9 shrink-0 overflow-hidden rounded-xs">{t8}</div>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== roomData.title) {
    t10 = <p className="truncate text-sm leading-5 font-semibold">{roomData.title}</p>;
    $[18] = roomData.title;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== roomData.owner.display_name) {
    t11 = <p className="truncate text-xs leading-4">{roomData.owner.display_name}</p>;
    $[20] = roomData.owner.display_name;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = <div className="min-w-0">{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t7 || $[27] !== t9) {
    t13 = <div className={t7}>{t9}{t12}</div>;
    $[25] = t12;
    $[26] = t7;
    $[27] = t9;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13 || $[30] !== t5) {
    t14 = <div data-component="full3d-mobile-sticky-banner-ad" className="bg-background relative h-[50px] shrink-0 overflow-hidden">{t5}{t13}</div>;
    $[29] = t13;
    $[30] = t5;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
}
export default Full3dMobileStickyBannerAd;